import React, { useEffect, useState, useCallback, useMemo } from "react";
import { throttle, debounce } from "throttle-debounce";
import _ from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Collapse } from "reactstrap";
import "./styles/multiselectTableV2.css";

/**
 * Pagination-enabled multiselect list/table.
 *
 * "getPaged" is an async function provided by the client (e.g., an
 * async dispatch), and has the following signature:
 * "getPaged(searchString, currPage, pageSize, refreshList)",
 * where refreshList is a boolean indicating whether to replace existing
 * dataset with incoming, or append incoming to the existing dataset.
 * (Typically true for handling searches, false for navigating to next
 * page.) All other params are states managed internally by this component.
 * Also, "dispatchSearchWaitingFor" is a required prop for pagination -
 * provide a (non async) store dispatch to set the "waiting for" value.
 *
 * If pagination not desired, provide searchType of "none" or "simple".
 * "simple" searchType will provide text search of all properties, for all
 * rows.
 *
 * This component manages selected items internally, and client is
 * expected to manage the selected item ids.
 *
 * Expandable rows: Set "expandable" prop to enable expand/collapse for rows.
 * Provide "expandedItemIds", "onToggleExpand", and "renderExpandedContent".
 */
export default function MultiselectTableV2(props) {
  const {
    data, // @type {Object[]}
    idPropName,
    columns, // @type {Object[]}
    updateSelectedItemIds,
    selectedItemIds, // @type {number[]}
    disabledItemIds, // @type {number[]}
    clientDeselectedItemId, // @type {number}
    setClientDeselectedItemId,
    searchType, // "none", "simple", or "paginated"
    searchPlaceholder, // Optional placeholder
    getPaged,
    dispatchSearchWaitingFor,
    addCallback,
    showAdd,
    getDeselectAllowed,
    toggleDeselectNotAllowedAlert,
    deselectCallback,
    className,
    // Expandable row props
    expandable, // @type {boolean} - Enable expandable rows
    expandedItemIds, // @type {number[]} - IDs of currently expanded items
    onToggleExpand, // @type {(itemId: number) => void} - Callback when expand toggled
    renderExpandedContent, // @type {(item: Object) => ReactNode} - Render expanded content
  } = props;

  const [sortColumn, setSortColumn] = useState({ path: columns[0].path, order: "asc" });
  const [searchString, setSearchString] = useState("");
  const [currPage, setCurrPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);

  const _data = useMemo(() => {
    if (searchType === "simple") {
      const filtered = (data || []).filter((d) =>
        // Serialize each entry for simple string search
        Object.entries(d)
          .filter(([key, value]) => {
            const col = columns.find((c) => c.path === key);
            return col && col.isSearchable;
          })
          .map(([key, value]) => `${value}`) // to string
          .join(" ")
          .toLowerCase()
          .includes((searchString || "").toLowerCase().trim())
      );
      return _.orderBy(filtered, [sortColumn.path], [sortColumn.order]);
    }
    // Otherwise, simply return the sorted array
    // ('paginated' searchType without searchString also applies. This will
    // happen when first loading or when searchString is cleared. For 'paginated'
    // with searchString, results are sorted by relevance and not by sortColumn.)
    else if (searchString.trim())
      return _.orderBy([...(data || [])], [sortColumn.path], [sortColumn.order]);
    else return [...(data || [])];
  }, [data, columns, searchString, sortColumn]);

  const [deselectedItemIds, setDeselectedItemIds] = useState([]);

  /**
   * handleSort(column):
   * Updates current sortColumn when a column's header is clicked.
   * _data will be updated in useEffect().
   */
  const handleSort = useCallback(
    (column) => {
      if (column.isSortable) {
        // Toggle asc/desc if clicked column is same as current sortColumn
        if (sortColumn.path === column.path)
          setSortColumn({
            path: column.path,
            order: sortColumn.order === "asc" ? "desc" : "asc",
          });
        else setSortColumn({ path: column.path, order: "asc" });
      }
    },
    [sortColumn]
  );

  /**
   * handleSelect(item):
   * Adds item.id to parent's selectedItemIds. If a recoverableItem is found,
   * also updates deselectedItemIds.
   * Note - setSelectedItems() will be called on useEffect() for handling
   * updated selectedItemIds.
   */
  const handleSelect = useCallback(
    (itemId) => {
      const recoverableItemExists = deselectedItemIds.includes((did) => did === itemId);
      if (recoverableItemExists)
        setDeselectedItemIds(deselectedItemIds.filter((did) => did !== itemId));
      updateSelectedItemIds([...selectedItemIds, itemId]);
    },
    [selectedItemIds, deselectedItemIds]
  );

  /**
   * handleDeselect(itemId):
   * Deletes item.id from parent's selectedItemIds, and adds item to deselectedItemIds.
   */
  const handleDeselect = useCallback(
    async (itemId) => {
      const deselectAllowed = await getDeselectAllowed(itemId);
      if (deselectAllowed) {
        updateSelectedItemIds(selectedItemIds.filter((sid) => sid !== itemId)); // Update parent
        setDeselectedItemIds([...deselectedItemIds, itemId]);
        deselectCallback && deselectCallback(itemId);
      } else {
        toggleDeselectNotAllowedAlert && toggleDeselectNotAllowedAlert();
      }
    },
    [selectedItemIds, deselectedItemIds]
  );

  /**
   * Update deselectedItemIds when client performs deselect.
   * Resets clientDeselectedItemId to null when update is completed.
   */
  useEffect(() => {
    if (
      clientDeselectedItemId &&
      !deselectedItemIds.includes((did) => did === clientDeselectedItemId)
    ) {
      handleDeselect(clientDeselectedItemId);
      setClientDeselectedItemId(null);
    }
  }, [clientDeselectedItemId, deselectedItemIds, idPropName]);

  useEffect(() => {
    if (searchType === "paginated") {
      if (!searchString) handlePaginatedSearch();
      else if (searchString.length < 3) searchThrottled();
      else searchDebounced();
    }
  }, [searchString]);

  /**
   * handlePaginatedSearch():
   * Fetches fresh paginated data when:
   * 1) Initializing (with blank searchString; see useEffect below)
   * 2) searchString changes (throttled/debounced)
   */
  const handlePaginatedSearch = useCallback(async () => {
    const refreshList = true;
    dispatchSearchWaitingFor(searchString || "");
    const pageCount = await getPaged(searchString || "", 1, 50, refreshList);
    setCurrPage(1);
    setPageCount(pageCount);
  }, [searchString]);

  const searchThrottled = throttle(200, handlePaginatedSearch);
  const searchDebounced = debounce(200, handlePaginatedSearch);

  useEffect(() => searchType === "paginated" && handlePaginatedSearch(), []);

  /**
   * getNextPage():
   * Fetches next page of data.
   */
  const getNextPage = useCallback(async () => {
    const nextPage = currPage + 1;
    const refreshList = false;
    setCurrPage(nextPage);
    const pageCount = await getPaged(searchString, nextPage, 100, refreshList);
    setPageCount(pageCount);
  }, [currPage, searchString]);

  const getSortIcon = useCallback(
    (column) => {
      if (column.isSortable) {
        return (
          <FontAwesomeIcon
            icon={
              column.path !== sortColumn.path
                ? "sort"
                : sortColumn.order === "asc"
                ? "sort-up"
                : "sort-down"
            }
            className="ml-1"
          />
        );
      }
      return "";
    },
    [sortColumn]
  );

  const isLastPage = useMemo(
    () => searchType === "paginated" && currPage >= pageCount,
    [currPage, pageCount, searchType]
  );

  return (
    <div className="d-flex flex-column">
      {searchType !== "none" && (
        <div className="form-inline mb-2">
          <div className="input-group w-100">
            <div className="input-group-prepend">
              <span className="input-group-text bg-lightgray">
                <FontAwesomeIcon icon="search" className="fa-fw text-muted" />
              </span>
            </div>
            <input
              type="search"
              name="searchString"
              id="multiselectV2_search"
              className="form-control"
              value={searchString}
              onChange={(e) => setSearchString(e.target.value)}
              placeholder={searchPlaceholder || "Search..."}
            />
          </div>
        </div>
      )}
      <div className={`w-100${className ? ` ${className}` : ""}`}>
        <div className="multiselectV2__table">
          <div
            className="d-flex flex-row w-100 py-1 bg-lightgray multiselectV2__table-header"
            style={{ zIndex: 9 }}
          >
            {expandable && (
              <div
                key="multiselectV2_thKey_expand"
                className="p-1 multiselectV2__table-col--expand"
              ></div>
            )}
            <div
              key="multiselectV2_thKey_check"
              className="p-1 multiselectV2__table-col--check"
            ></div>
            {columns.map((c) => (
              <div
                className="pointer-cursor bg-lightgray font-weight-bold"
                key={`multiselectV2_thKey_${c.path || c.key}`}
                onClick={() => handleSort(c)}
                style={{ width: c.width }}
              >
                {c.label}
                {getSortIcon(c)}
              </div>
            ))}
          </div>
          <MultiselectTableV2_TableBody
            data={_data}
            idPropName={idPropName}
            columns={columns}
            selectedItemIds={selectedItemIds}
            handleSelect={handleSelect}
            deselectedItemIds={deselectedItemIds}
            handleDeselect={handleDeselect}
            disabledItemIds={disabledItemIds}
            searchString={searchString}
            searchType={searchType}
            isLastPage={isLastPage}
            getNextPage={getNextPage}
            showAdd={showAdd}
            addCallback={addCallback}
            expandable={expandable}
            expandedItemIds={expandedItemIds}
            onToggleExpand={onToggleExpand}
            renderExpandedContent={renderExpandedContent}
          />
        </div>
      </div>
    </div>
  );
}

function MultiselectTableV2_TableBody(props) {
  const {
    data,
    idPropName,
    columns,
    selectedItemIds,
    handleSelect,
    deselectedItemIds,
    handleDeselect,
    disabledItemIds,
    searchType,
    searchString,
    isLastPage,
    getNextPage,
    showAdd,
    addCallback,
    expandable,
    expandedItemIds,
    onToggleExpand,
    renderExpandedContent,
  } = props;

  const isExpanded = useCallback(
    (id) => expandable && (expandedItemIds || []).includes(id),
    [expandable, expandedItemIds]
  );

  const handleClick_expand = (itemId) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleExpand && onToggleExpand(itemId);
  };

  const isSelected = useCallback(
    (id) => (selectedItemIds || []).includes(id),
    [selectedItemIds, idPropName]
  );

  const isDeselected = useCallback(
    (id) => (deselectedItemIds || []).includes(id),
    [deselectedItemIds, idPropName]
  );

  const isDisabled = useCallback(
    (id) => (disabledItemIds || []).includes(id),
    [disabledItemIds, idPropName]
  );

  const getTrClass = useCallback(
    (id) => {
      let trClass = "d-flex flex-row py-1 multiselectV2__table-row";
      if (isDisabled(id)) trClass = trClass + " cursor-not-allowed text-muted bg-white";
      else {
        if (isSelected(id)) trClass = trClass + " table-primary";
        else if (isDeselected(id)) trClass = trClass + " table-warning";
        trClass = trClass + " pointer-cursor";
      }
      return trClass;
    },
    [isDisabled, isSelected, isDeselected, idPropName]
  );

  const handleClick_tr = (itemId) => (e) => {
    e.preventDefault();
    // For touch devices, only capture "taps" - not scrolling movements
    if (!isDisabled(itemId)) {
      // If item is already selected, then remove
      if ((selectedItemIds || []).includes(itemId)) handleDeselect(itemId);
      else handleSelect(itemId);
    }
  };

  const handleClick_showMore = (e) => {
    e.preventDefault();
    getNextPage();
  };

  const handleClick_add = (e) => {
    e.preventDefault();
    addCallback && addCallback(searchString);
  };

  const renderCheck = useCallback(
    (itemId) => {
      let icon;
      if (isSelected(itemId))
        icon = <FontAwesomeIcon icon="check" className="fa-fw text-primary" />;
      else if (isDeselected(itemId))
        icon = <FontAwesomeIcon icon="times" className="fa-fw text-warning" />;
      else icon = <FontAwesomeIcon icon="check" className="fa-fw icon-invisible" />;

      // Override prev two conditions with isDisabled
      if (isDisabled(itemId) && isSelected(itemId)) {
        icon = <FontAwesomeIcon icon="check" className="fa-fw text-muted" />;
      }
      return (
        <div
          key={`multiselectV2_tdKey_checkCol`}
          className="multiselectV2__table-col--check mx-auto"
        >
          {icon}
        </div>
      );
    },
    [isSelected, isDeselected, isDisabled, idPropName]
  );

  const renderCell = useCallback((item, column) => {
    if (column.content) return column.content(item);
    return _.get(item, column.path);
  }, []);

  const createTdKey = useCallback(
    (itemId, column) => `multiselectV2_tdKey_${column.path || column.key}_${itemId}`,
    [idPropName]
  );

  const renderExpandButton = (itemId) => {
    if (!expandable) return null;
    return (
      <div
        key={`multiselectV2_tdKey_expandCol_${itemId}`}
        className="multiselectV2__table-col--expand"
        onClick={handleClick_expand(itemId)}
      >
        <FontAwesomeIcon
          icon={isExpanded(itemId) ? "caret-up" : "caret-down"}
          className="fa-fw text-secondary"
        />
      </div>
    );
  };

  return (
    <div className="multiselectV2__table-body">
      {(data || []).map((item, index) => (
        <React.Fragment key={`multiselectV2_trKey_${index}_${item[idPropName]}`}>
          <div
            className={getTrClass(item[idPropName])}
            onClick={handleClick_tr(item[idPropName])}
          >
            {renderExpandButton(item[idPropName])}
            {renderCheck(item[idPropName])}
            {columns.map((column) => (
              <div
                key={createTdKey(item[idPropName], column)}
                style={{ width: column.width || "auto" }}
              >
                {renderCell(item, column)}
              </div>
            ))}
          </div>
          {expandable && (
            <Collapse isOpen={isExpanded(item[idPropName])}>
              <div className="multiselectV2__expanded-content px-2 py-1 bg-light border-bottom">
                {renderExpandedContent && renderExpandedContent(item)}
              </div>
            </Collapse>
          )}
        </React.Fragment>
      ))}
      {searchType === "paginated" && !isLastPage && (
        <div key={`multiselectV2_trKey_showMore`}>
          <div className="w-100" style={{ textAlign: "center" }}>
            <button
              name="showMore"
              id="showMore"
              className="btn btn-sm btn-link py-0"
              onClick={handleClick_showMore}
            >
              Show More...
            </button>
          </div>
        </div>
      )}
      {showAdd ? (
        <div key="multiselectV2_trKey_add">
          <div className="w-100" style={{ textAlign: "center" }}>
            <button
              name="showAdd"
              id="showAdd"
              className="btn btn-sm btn-link py-0"
              onClick={handleClick_add}
            >
              {`Add \"${searchString}\"...`}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
